class ThwVehicleFinder extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.abortController = new AbortController();

    this.form = this.querySelector('[data-fitment-form]');
    this.dataElement = this.querySelector('[data-fitment-data]');
    this.status = this.querySelector('[data-fitment-status]');
    this.year = this.querySelector('[data-fitment-year]');
    this.make = this.querySelector('[data-fitment-make]');
    this.model = this.querySelector('[data-fitment-model]');
    this.category = this.querySelector('[data-fitment-category]');
    this.queryInput = this.querySelector('[data-fitment-query]');
    this.displayQueryInput = this.querySelector('[data-fitment-display-query]');
    this.submitButton = this.querySelector('[data-fitment-submit]');
    this.clearButton = this.querySelector('[data-fitment-clear]');
    this.storage = this.getAvailableStorage();
    this.storageKey = 'thwVehicleFitmentV2';
    // Bump this when the row normalisation changes so malformed legacy values
    // are not retained in an existing browser cache.
    this.cacheKey = 'thwVehicleFitmentIndexV5';
    this.cacheTtl = 10 * 60 * 1000;
    this.fitmentIndexUrl = this.dataset.fitmentIndexUrl || '';
    this.clearUrl = this.dataset.fitmentClearUrl || '/collections/all?sort_by=created-descending';

    if (!this.form || !this.dataElement || !this.status || !this.year || !this.make || !this.model || !this.category || !this.queryInput || !this.displayQueryInput || !this.submitButton || !this.clearButton) {
      return;
    }

    this.bindEvents();
    document.addEventListener('thw:clear-fitment', () => this.clearSelection(), { signal: this.abortController.signal });
    window.requestAnimationFrame(() => this.initialiseOptions());
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    this.initialised = false;
  }

  getAvailableStorage() {
    const candidates = [
      () => window.localStorage,
      () => window.sessionStorage
    ];

    for (const getStorage of candidates) {
      try {
        const storage = getStorage();
        const testKey = '__thwFitmentStorageTest';
        storage.setItem(testKey, '1');
        storage.removeItem(testKey);
        return storage;
      } catch (error) {
        // Continue without persistence if browser storage is unavailable.
      }
    }

    return null;
  }

  bindEvents() {
    const signal = this.abortController.signal;
    this.year.addEventListener('change', () => this.handleYearChange(), { signal });
    this.make.addEventListener('change', () => this.handleMakeChange(), { signal });
    this.model.addEventListener('change', () => this.handleModelChange(), { signal });
    this.category.addEventListener('change', () => this.handleCategoryChange(), { signal });
    this.clearButton.addEventListener('click', () => this.clearSelection(), { signal });
    this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
  }

  async initialiseOptions() {
    try {
      const parsedRows = await this.loadAllFitmentRows();
      const uniqueRows = new Map();
      parsedRows.forEach((row) => {
        const key = [row.year, row.make, row.model, row.category]
          .map((value) => String(value || '').trim().toLowerCase())
          .join('|');
        if (key !== '|||') uniqueRows.set(key, row);
      });

      const expandedRows = [...uniqueRows.values()]
        .map((row) => ({
          sourceYear: String(row.year || '').trim(),
          make: String(row.make || '').trim(),
          model: String(row.model || '').trim(),
          category: String(row.category || '').trim()
        }))
        .filter((row) => row.sourceYear && row.make && row.model && row.category)
        .flatMap((row) => this.expandYearRow(row));

      const uniqueExpandedRows = new Map();
      expandedRows.forEach((row) => {
        const key = [row.year, row.sourceYear, row.make, row.model, row.category].join('|');
        uniqueExpandedRows.set(key, row);
      });
      this.rows = [...uniqueExpandedRows.values()];

      if (!this.rows.length) {
        this.setUnavailableState('Unavailable: No verified vehicle fitment data is available yet. Browse all parts or contact the team for help.');
        return;
      }

      const saved = this.loadSelection();
      this.populateSelect(this.year, this.uniqueValues(this.rows, 'year', true), 'Choose a year');

      if (saved.year && this.hasOption(this.year, saved.year)) {
        this.year.value = saved.year;
        this.populateMakes(saved.make, saved.model, saved.category);
      } else {
        this.resetSelect(this.make, 'Choose a year first');
        this.resetSelect(this.model, 'Choose a make first');
        this.resetSelect(this.category, 'Choose a model first');
      }

      this.updateSubmitState();
      this.updateInstruction();
      this.setAttribute('aria-busy', 'false');
    } catch (error) {
      this.setUnavailableState('Error: The vehicle finder could not load. Browse all parts or try again after refreshing the page.', 'error');
    }
  }

  async loadAllFitmentRows() {
    const cachedRows = this.loadFitmentCache();
    if (cachedRows) return cachedRows;

    if (this.fitmentIndexUrl) {
      try {
        const response = await fetch(this.fitmentIndexUrl, {
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal
        });
        if (response.ok) {
          const indexedRows = this.parseFitmentIndex(await response.text());
          if (indexedRows) {
            this.saveFitmentCache(indexedRows);
            return indexedRows;
          }
        }
      } catch (error) {
        // Use the small inline fallback below when the index is unavailable.
      }
    }

    const inlineRows = this.parseFitmentRows(this.dataElement.textContent || '[]');
    this.saveFitmentCache(inlineRows);
    return inlineRows;
  }

  loadFitmentCache() {
    if (!this.storage) return null;

    try {
      const cached = JSON.parse(this.storage.getItem(this.cacheKey) || 'null');
      const isCurrent = cached && Array.isArray(cached.rows);
      const isFresh = cached && Date.now() - cached.createdAt < this.cacheTtl;
      if (isCurrent && isFresh && Array.isArray(cached.rows)) return cached.rows;
    } catch (error) {
      // Ignore an invalid or unavailable cache and load fresh data.
    }

    return null;
  }

  saveFitmentCache(rows) {
    if (!this.storage) return;

    try {
      this.storage.setItem(this.cacheKey, JSON.stringify({
        createdAt: Date.now(),
        rows
      }));
    } catch (error) {
      // The finder remains usable when browser storage is unavailable or full.
    }
  }

  parseFitmentRows(value) {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) throw new Error('Fitment catalogue data is not an array');
    return parsed;
  }

  parseFitmentIndex(value) {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    if (!parsed || !Array.isArray(parsed.rows)) return null;
    return parsed.rows;
  }

  expandYearRow(row) {
    const sourceYear = this.normaliseYearSource(row.sourceYear);
    const range = sourceYear.match(/^(?:(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\s+)?(\d{4})\s*(?:[-\u2013\u2014]|to)\s*(?:(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\s+)?(\d{4}|current)$/i);
    if (!range) {
      return /^\d{4}$/.test(sourceYear) ? [{ ...row, year: sourceYear }] : [];
    }

    const startYear = Number(range[1]);
    // "Current" keeps rolling without an annual catalogue or theme edit while
    // searches retain the exact source phrase.
    const endYear = range[2].toLowerCase() === 'current'
      ? new Date().getFullYear()
      : Number(range[2]);
    if (startYear > endYear || endYear - startYear > 100) {
      return [{ ...row, year: row.sourceYear }];
    }

    return Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => ({ ...row, year: String(endYear - index) })
    );
  }

  normaliseYearSource(value) {
    return String(value || '')
      .trim()
      // Some legacy year metafields contain an internal option suffix such as
      // "2021 --1". Keep sourceYear unchanged for the eventual search filter,
      // but remove the suffix from the value shown to customers.
      .replace(/^(\d{4})\s+--\d+$/, '$1');
  }

  populateMakes(preferredMake = '', preferredModel = '', preferredCategory = '') {
    const matches = this.rows.filter((row) => row.year === this.year.value);
    this.populateSelect(this.make, this.uniqueValues(matches, 'make'), 'Choose a make');
    this.resetSelect(this.model, 'Choose a make first');
    this.resetSelect(this.category, 'Choose a model first');

    if (preferredMake && this.hasOption(this.make, preferredMake)) {
      this.make.value = preferredMake;
      this.populateModels(preferredModel, preferredCategory);
    }
  }

  populateModels(preferredModel = '', preferredCategory = '') {
    const matches = this.rows.filter((row) => row.year === this.year.value && row.make === this.make.value);
    this.populateSelect(this.model, this.uniqueValues(matches, 'model'), 'Choose a model');
    this.resetSelect(this.category, 'Choose a model first');

    if (preferredModel && this.hasOption(this.model, preferredModel)) {
      this.model.value = preferredModel;
      this.populateCategories(preferredCategory);
    }
  }

  populateCategories(preferredCategory = '') {
    const matches = this.rows.filter((row) => (
      row.year === this.year.value &&
      row.make === this.make.value &&
      row.model === this.model.value
    ));
    this.populateSelect(this.category, this.uniqueValues(matches, 'category'), 'All parts (optional)');

    if (preferredCategory && this.hasOption(this.category, preferredCategory)) {
      this.category.value = preferredCategory;
    }
  }

  handleYearChange() {
    this.populateMakes();
    this.persistSelection();
    this.updateSubmitState();
    this.updateInstruction();
  }

  handleMakeChange() {
    this.populateModels();
    this.persistSelection();
    this.updateSubmitState();
    this.updateInstruction();
  }

  handleModelChange() {
    this.populateCategories();
    this.persistSelection();
    this.updateSubmitState();
    this.updateInstruction();
  }

  handleCategoryChange() {
    this.persistSelection();
    this.updateSubmitState();
    this.updateInstruction();
  }

  clearSelection() {
    if (!this.rows || !this.rows.length) return;

    if (this.storage) {
      try {
        this.storage.removeItem(this.storageKey);
      } catch (error) {
        // The finder can still be reset when browser storage is unavailable.
      }
    }

    this.queryInput.value = '';
    this.displayQueryInput.value = '';
    this.populateSelect(this.year, this.uniqueValues(this.rows, 'year', true), 'Choose a year');
    this.resetSelect(this.make, 'Choose a year first');
    this.resetSelect(this.model, 'Choose a make first');
    this.resetSelect(this.category, 'Choose a model first');
    this.updateSubmitState();
    this.updateInstruction();
    const filterForm = this.closest('.thw-search-page__finder')?.querySelector('[data-storefront-filter-form]');
    if (filterForm) {
      filterForm.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
      filterForm.querySelectorAll('input[type="number"]').forEach((input) => { input.value = ''; });
    }
    this.year.focus();
  }

  handleSubmit(event) {
    const selection = this.currentSelection();
    if (!selection.year || !selection.make || !selection.model) {
      event.preventDefault();
      this.setStatus('error', 'Error: Complete vehicle year, make and model before searching.');
      this.firstIncompleteField().focus();
      return;
    }

    const matchingRows = this.rows.filter((row) => (
      row.year === selection.year &&
      row.make === selection.make &&
      row.model === selection.model &&
      (!selection.category || row.category === selection.category)
    ));
    if (!matchingRows.length) {
      event.preventDefault();
      this.setStatus('error', 'Error: This vehicle selection is no longer available. Clear the search and try again.');
      return;
    }

    const sourceYears = this.uniqueValues(matchingRows, 'sourceYear', true);
    event.preventDefault();
    // Keep the broad make/model terms in Shopify's search query. Shopify's
    // active native filter stores approved ranges as exact text values, so
    // submit every source range that covers the selected individual year.
    this.queryInput.value = `${selection.make} ${selection.model}`;
    this.displayQueryInput.value = [selection.year, selection.make, selection.model, selection.category].filter(Boolean).join(' ');
    this.persistSelection();
    this.submitButton.disabled = true;
    const resultsUrl = new URL(this.form.action, window.location.origin);
    resultsUrl.searchParams.set('q', this.queryInput.value);
    resultsUrl.searchParams.set('type', 'product');
    resultsUrl.searchParams.set('thw_q', this.displayQueryInput.value);
    resultsUrl.searchParams.set('filter.p.m.thw.make', selection.make);
    resultsUrl.searchParams.set('filter.p.m.thw.model', selection.model);
    sourceYears.forEach((sourceYear) => resultsUrl.searchParams.append('filter.p.m.thw.year', sourceYear));
    if (selection.category) resultsUrl.searchParams.set('filter.p.product_type', selection.category);
    window.location.assign(resultsUrl.href);
    this.setStatus('loading', 'Loading: Searching available product listings…');
  }

  quoteSearchValue(value) {
    const escaped = String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `"${escaped}"`;
  }

  populateSelect(select, values, placeholder) {
    select.replaceChildren();
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    placeholderOption.selected = true;
    select.append(placeholderOption);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });

    select.disabled = values.length === 0;
  }

  resetSelect(select, placeholder) {
    this.populateSelect(select, [], placeholder);
  }

  uniqueValues(rows, key, descending = false) {
    const values = [...new Set(rows.map((row) => row[key]))];
    return values.sort((a, b) => descending
      ? b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' })
      : a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  hasOption(select, value) {
    return Array.from(select.options).some((option) => option.value === value);
  }

  currentSelection() {
    return {
      year: this.year.value,
      make: this.make.value,
      model: this.model.value,
      category: this.category.value
    };
  }

  firstIncompleteField() {
    return [this.year, this.make, this.model].find((field) => !field.value) || this.year;
  }

  updateSubmitState() {
    const { year, make, model } = this.currentSelection();
    const selection = [year, make, model];
    this.submitButton.disabled = selection.some((value) => !value);
    this.clearButton.disabled = Object.values(this.currentSelection()).every((value) => !value);
  }

  updateInstruction() {
    const selection = this.currentSelection();
    if (!selection.year) this.setStatus('ready', 'Select a year to begin.');
    else if (!selection.make) this.setStatus('ready', 'Now choose a make.');
    else if (!selection.model) this.setStatus('ready', 'Now choose a model.');
    else if (!selection.category) this.setStatus('ready', `${selection.year} ${selection.make} ${selection.model} is ready to search. Choose a category to narrow the results, or search all matching parts.`);
    else this.setStatus('ready', `${selection.year} ${selection.make} ${selection.model} is ready to search.`);
  }

  setUnavailableState(message, kind = 'empty') {
    this.resetSelect(this.year, 'No verified years available');
    this.resetSelect(this.make, 'Choose a year first');
    this.resetSelect(this.model, 'Choose a make first');
    this.resetSelect(this.category, 'Choose a model first');
    this.submitButton.disabled = true;
    this.clearButton.disabled = true;
    this.setAttribute('aria-busy', 'false');
    this.setStatus(kind, message);
  }

  setStatus(kind, message) {
    this.dataset.state = kind;
    this.status.dataset.kind = kind;
    this.status.textContent = message;
    this.setAttribute('aria-busy', kind === 'loading' ? 'true' : 'false');
  }

  loadSelection() {
    if (!this.storage) return {};
    try {
      return JSON.parse(this.storage.getItem(this.storageKey) || '{}');
    } catch (error) {
      return {};
    }
  }

  persistSelection() {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(this.currentSelection()));
    } catch (error) {
      // The finder remains usable if persistence is blocked or storage is full.
    }
  }
}

if (!customElements.get('thw-vehicle-finder')) {
  customElements.define('thw-vehicle-finder', ThwVehicleFinder);
}
